import React, { useState } from 'react';
import { useAuthContext } from '../AuthProvider';
import Card from '../Card';
import GoogleIcon from '../GoogleIcon';
import { BrainCircuit, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { NAV_LINKS } from '../landing/constants';
import Button from '../landing/components/ui/Button';
import { trackEvent } from '../../utils/analytics';
import './LoginPage.css';

interface LoginPageProps {
  isOpen: boolean;
  onLoginSuccess?: (user: any) => void;
  onClose?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ isOpen, onLoginSuccess, onClose }) => {
  const { signInWithGoogle, error: authError, clearError } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    clearError();
    
    try {
      const user = await signInWithGoogle();
      trackEvent('login', { method: 'google' });
      onLoginSuccess?.(user);
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Show auth error if it exists
  React.useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // If not open and not being used as a full-page login, return null
  if (!isOpen) return null;

  return (
    <div className="login-page">
      {/* Header - Same as landing page */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-lg border-b border-slate-900/10">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 text-2xl font-bold text-slate-800">
            <BrainCircuit className="w-8 h-8 text-emerald-500" />
            <span>ThreadIt</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-slate-600 hover:text-emerald-500 transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:block">
            <Button to="/app">Login</Button>
          </div>

          <div className="md:hidden">
            <button onClick={toggleMenu} aria-label="Toggle menu" className="text-slate-800">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-lg absolute w-full left-0">
            <div className="flex flex-col items-center space-y-4 py-8">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-xl text-slate-600 hover:text-emerald-500 transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <Button to="/app" onClick={() => setIsMenuOpen(false)} className="mt-4">Login</Button>
            </div>
          </div>
        )}
      </header>

      <div className="login-container">
        <Card className="login-card-new">
          <div className="login-header">
            <h1 className="login-title">
              Log in to your account
            </h1>
            <p className="login-subtitle">
              Welcome back! Sign in with your Google account to continue.
            </p>
          </div>
          
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="google-button"
          >
            {isLoading ? (
              <div className="button-content">
                <div className="loading-spinner"></div>
                <span>Signing in...</span>
              </div>
            ) : (
              <>
                <GoogleIcon className="google-icon-new" />
                Sign in with Google
              </>
            )}
          </button>

          {/* Error message */}
          {error && (
            <div className="error-message-new">
              <svg className="error-icon-new" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Terms and privacy */}
          <div className="terms-section-new">
            <p className="terms-text-new">
              By continuing, you agree to our{' '}
              <a href="/terms" className="terms-link-new">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="terms-link-new">Privacy Policy</a>
            </p>
          </div>
        </Card>

        {/* Close button (if provided) */}
        {onClose && (
          <button 
            className="close-button-new" 
            onClick={onClose} 
            aria-label="Close login"
          >
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="close-icon">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
